import React, { useContext, useEffect, useState } from "react";
import { Dropdown, Form, Input, InputNumber, Modal, Tooltip, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import '../../../../styles/component/employee/employee-search.scss'
import server from "../../../../server";
import Grid from "../../../../common/table";
import resources from "../../../../resources";
import UserLoginContext from "../../../../context/userLoginContext";
import { IEditableCellProps, IItem } from "./interface";
import Locale from "../../../../core/locale/locale";
import dayjs from "dayjs";
import EmployeeAvatar from "../../avatar/employee-avatar";
import { Employee_Sort_Field } from "../../../../constant/constant";
import EmployeeOverviewPopover from "../../overview/employee-overview-popover/employee-overview-popover";
import DeactivateEmployeeStepper from "../../deactivate-employee/deactive-employee-stepper";
import { CircleX, Ellipsis, Eye, Pencil } from "lucide-react";

const EditableCell: React.FC<IEditableCellProps> = ({editing, dataIndex, title, inputType, record, index, children, ...restProps}) => {
    const inputNode = inputType === "number" ? <InputNumber/> : <Input/>;
    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{margin: 0}}
                    rules={[
                        {
                            required: true,
                            message: `Please Input ${title}!`,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const EmployeeSearchTable = ({employeeResponse, isActiveEmployees, pageNumber, pageSize, handleNextPage, handlePrevPage, onSortChange, sortField, sortDirection}: any) => {
    // const initialData = JSON.parse(JSON.stringify(employeeResponse));
    const [data, setData] = useState(employeeResponse);
    const [editingKey, setEditingKey] = useState("");
    const isEditing = (record: IItem) => record.key === editingKey;
    const {user} = useContext(UserLoginContext);
    const navigate = useNavigate();
    const tempResource = resources.searchEmployeeTable;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reportees, setReportees] = useState<any[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<IItem | null>(null);
    const [modalForm] = Form.useForm();


    const getAllReportees = async (record: any) => {
        setSelectedRecord(record);
        try {
            const reportees = await server.employee.getAllReportees(record.id, 0, 50);
            setReportees(reportees);
            setIsModalOpen(true);
        } catch (error) {
            console.log("Error fetching reportees or employees:", error);
        }
    };

    // const markEmployeeInActive = async (prevManagerId: string, reportees: any, newManagerId: string) => {
    //     try {
    //         for (const reportee of reportees) {
    //             await server.employee.admin.assignSupervisor(reportee.id, newManagerId);
    //         }
    //         await server.employee.admin.markEmployeeInActive(prevManagerId);
    //         const newData = data.filter((item: any) => item.id !== prevManagerId);
    //         setData(newData);
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    const columns = [
        {
            title: tempResource.tableColumns.employeeCode,
            dataIndex: "employeeCode",
            width: "15%",
            key: "employeeCode",
            editable: true,
            sorter: true,
            sortField: Employee_Sort_Field.employeeCode,
            sortOrder: sortField === Employee_Sort_Field.employeeCode
                ? sortDirection === "ASC"
                    ? "ascend"
                    : sortDirection === "DESC"
                        ? "descend"
                        : null
                : null,
            filterable: false,
        },
        {
            title: tempResource.tableColumns.name,
            dataIndex: "employeeName",
            key: "employeeName",
            editable: true,
            filterable: false,
            sorter: true,
            sortField: Employee_Sort_Field.firstName,
            sortOrder: sortField === Employee_Sort_Field.firstName
                ? sortDirection === "ASC"
                    ? "ascend"
                    : sortDirection === "DESC"
                        ? "descend"
                        : null
                : null,
            render: (_: any, record: any) => (
                <div className="employee-name-wrapper">
                    <EmployeeAvatar
                        name={record.name}
                        thumbnailId={record.profilePhotoThumbnailId}
                        size={25}
                    />
                    <EmployeeOverviewPopover employeeId={record.id}>
                        <div className="employee-search-table-name">
                            {record.name?.firstName} {record.name?.lastName}
                        </div>
                    </EmployeeOverviewPopover>
                </div>
            )
        },
        {
            title: tempResource.tableColumns.designation,
            dataIndex: "designation",
            key: "designation",
            editable: true,
            filterable: false,
            sorter: false,
            render: (_: any, record: any) => record?.designation ?? "-"
        },
        {
            title: tempResource.tableColumns.contact,
            dataIndex: "mobileNumber",
            key: "mobileNumber",
            editable: true,
            render: (data: any) => {
                return `${data.countryCode ? data.countryCode : ""} ${data.number ? data.number : '-'}`
            }
        },
        {
            title: tempResource.tableColumns.email,
            dataIndex: "officialEmail",
            key: "officialEmail",
            editable: true,
            sorter: true,
            sortField: Employee_Sort_Field.officialEmail,
            sortOrder: sortField === Employee_Sort_Field.officialEmail
                ? sortDirection === "ASC"
                    ? "ascend"
                    : sortDirection === "DESC"
                        ? "descend"
                        : null
                : null,
            filterable: false,
        },
        {
            title: tempResource.tableColumns.joiningDate,
            dataIndex: "joiningDate",
            key: "joiningDate",
            editable: true,
            sorter: true,
            sortField: Employee_Sort_Field.joiningDate,
            sortOrder: sortField === Employee_Sort_Field.joiningDate
                ? sortDirection === "ASC"
                    ? "ascend"
                    : sortDirection === "DESC"
                        ? "descend"
                        : null
                : null,
            render: (_: any, employeeResponse: any) =>
                employeeResponse?.joiningDate ? dayjs(employeeResponse?.joiningDate).format(Locale.displayDate) : "-"
        },
        ...(user?.isAdmin ? [{
                title: tempResource.tableColumns.actions,
                dataIndex: "tableActions",
                width: "5%",
                key: "tableActions",
                render: (_: any, record: IItem) => {
                    const menuItems = [
                        {
                            label: 'View',
                            key: 0,
                            icon: <Eye
                                className={`action-view ${editingKey !== "" ? 'action-disabled' : ''}`}/>,
                            onClick: () => {
                                navigate('/employee/detail', {
                                    state: {isActive: isActiveEmployees, empId: record.id}
                                })
                            }
                        },
                        ...(isActiveEmployees ? [{
                            label: 'Edit',
                            key: 1,
                            onClick: () => {
                                navigate(
                                    '/employee/edit/personal', {
                                        state: {empId: record.id}
                                    })
                            },
                            icon: <Pencil
                                className={`action-edit ${editingKey !== "" ? 'action-disabled' : ''}`}/>
                        }] : []),

                        {
                            label: 'Deactivate',
                            key: 2,
                            onClick: () => {
                                getAllReportees(record)
                            },
                            icon: <CircleX
                                className={"action-delete"}
                            />,
                        }
                    ]
                    return (
                        <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                            <Ellipsis className={'pn-table-actions-icon'}/>
                        </Dropdown>
                    );
                },
            }] : []
        )
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: IItem) => ({
                record,
                inputType: col.dataIndex === "age" ? "number" : "text",
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    useEffect(() => {
        setData(employeeResponse);
    }, [employeeResponse]);

    return (
        <>
            <div className={'employee-search-data-table'}>
                <Grid
                    components={{
                        body: {
                            cell: EditableCell,
                        },
                    }}
                    rowKey="id"
                    bordered={true}
                    onChange={(pagination: any, filters: any, sorter: any) => {
                        let direction: "ASC" | "DESC" | undefined;

                        switch (sorter.order) {
                            case "ascend":
                                direction = "ASC";
                                break;
                            case "descend":
                                direction = "DESC";
                                break;
                            default:
                                direction = undefined;
                        }
                        const backendField = sorter.column?.sortField ?? Employee_Sort_Field.joiningDate;

                        if (!backendField) return;
                        onSortChange(backendField, direction);
                    }}
                    dataSource={data}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    pageNumber={pageNumber}
                    paginationButton={true}
                    pageSize={pageSize}
                    handleNextPage={handleNextPage}
                    handlePrevPage={handlePrevPage}
                />
            </div>

            <Modal
                className="hrms-app deactivate-employee-modal"
                title={<span
                    style={{color: '#E86C6A'}}>{`Deactivate ${selectedRecord?.name.firstName} ${selectedRecord?.name.lastName} `}</span>}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    modalForm.resetFields();
                }}
                footer={null}
            >
                <DeactivateEmployeeStepper
                    reportees={reportees}
                    selectedRecord={selectedRecord}
                    modalForm={modalForm}
                    data={data}
                    setData={setData}
                    setIsModalOpen={setIsModalOpen}
                    getAllReportees={getAllReportees}
                />
            </Modal>
        </>
    );
};

export default EmployeeSearchTable;
